import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, ShoppingCart, User, ShieldCheck, Smartphone, Download, Truck } from 'lucide-react';
import { useCart, useSiteSettings, useLanguage } from '../../contexts/SiteContexts';
import { useAuth } from '../../contexts/AuthContext';
import { LOGO_ICON } from '../../assets/logos';
import { isMobileAppEnvironment } from '../../utils/appDetection';

export const Navbar = () => {
  const { cart, setIsCartOpen } = useCart();
  const { settings } = useSiteSettings();
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const [isInsideMobileApp, setIsInsideMobileApp] = useState(false);

  useEffect(() => {
    setIsInsideMobileApp(isMobileAppEnvironment());
  }, []);

  const clickCountRef = useRef(0);
  const clickTimerRef = useRef<NodeJS.Timeout | null>(null);

  const handleLogoSecretClick = (e: React.MouseEvent) => {
    clickCountRef.current += 1;
    if (clickTimerRef.current) clearTimeout(clickTimerRef.current);

    if (clickCountRef.current >= 10) {
      clickCountRef.current = 0;
      navigate('/admin/login');
      return;
    }

    // Reset counter if no click within 4 seconds
    clickTimerRef.current = setTimeout(() => {
      clickCountRef.current = 0;
    }, 4000);
  };

  return (
    <nav className="sticky top-0 z-[100] bg-white/95 backdrop-blur-md px-4 md:px-10 h-20 md:h-24 flex items-center justify-between border-b border-slate-100 shadow-sm">
      <div className="flex items-center space-x-3 md:space-x-4 cursor-pointer select-none" onClick={handleLogoSecretClick}>
        <div className="w-11 h-11 md:w-14 md:h-14 rounded-2xl bg-white border border-slate-100 flex items-center justify-center p-2.5 md:p-3 shadow-xl shadow-slate-100/50 group active:scale-95 transition-all">
          <img src={settings.logoIconUrl || LOGO_ICON} alt="Hamim Enterprises" className="w-full h-full object-contain" />
        </div>
        <div className="flex flex-col">
          <span className="font-black tracking-tighter text-[10px] md:text-[11px] uppercase text-slate-400 leading-none mb-1">Elite Surgical Store</span>
          <span className="font-black tracking-tighter text-base md:text-xl uppercase text-slate-900 leading-none">HAMIM <span className="text-red-600">ENTERPRISES</span></span>
        </div>
      </div>

      <div className="hidden md:flex items-center space-x-7">
        <Link to="/" className="text-sm font-black uppercase tracking-widest text-slate-900 hover:text-indigo-600 transition-colors">{t('home')}</Link>
        <Link to="/categories" className="text-sm font-black uppercase tracking-widest text-slate-900 hover:text-indigo-600 transition-colors">{t('categories')}</Link>
        <Link to="/tracking" className="text-sm font-black uppercase tracking-widest text-indigo-600 hover:text-indigo-700 transition-colors flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
          Track
        </Link>
        <Link to="/doctor-hub" className="text-sm font-black uppercase tracking-widest text-slate-900 hover:text-indigo-600 transition-colors flex items-center gap-1">
          <ShieldCheck size={16} className="text-indigo-600" />
          Dr to Dr
        </Link>
        <Link to="/blogs" className="text-sm font-black uppercase tracking-widest text-slate-900 hover:text-indigo-600 transition-colors">{t('blog')}</Link>
        <Link to="/needs" className="text-sm font-black uppercase tracking-widest text-slate-900 hover:text-indigo-600 transition-colors">{t('needs')}</Link>
        
        {/* Only show on Website, auto-hide if opened inside mobile app */}
        {!isInsideMobileApp && settings.appBannerEnabled !== false && (
          <Link 
            to="/download" 
            className="text-xs font-black uppercase tracking-wider text-emerald-700 hover:text-emerald-800 transition-all flex items-center gap-1.5 px-3.5 py-2 bg-emerald-50 hover:bg-emerald-100/80 rounded-xl border border-emerald-200/60 shadow-sm"
          >
            <Download size={14} className="text-emerald-600" />
            <span>Download APK</span>
          </Link>
        )}
      </div>

      <div className="flex items-center space-x-2 md:space-x-4">
        {/* Mobile Quick Download APK Icon (only on website) */}
        {!isInsideMobileApp && settings.appBannerEnabled !== false && (
          <Link 
            to="/download"
            className="md:hidden p-2.5 bg-emerald-50 text-emerald-700 rounded-2xl hover:bg-emerald-100 transition-all flex items-center gap-1 border border-emerald-200"
            title="Download Hamim Enterprises APK"
          >
            <Download size={16} />
          </Link>
        )}

        <button 
          onClick={() => navigate('/search')}
          className="p-2.5 md:p-3 bg-slate-50 text-slate-600 rounded-2xl hover:bg-slate-100 transition-all"
        >
          <Search size={18} className="md:w-5 md:h-5" />
        </button>
        <button 
          onClick={() => navigate('/tracking')}
          className="p-2.5 md:p-3 bg-slate-50 text-slate-600 rounded-2xl hover:bg-slate-100 transition-all"
        >
          <Truck size={18} className="md:w-5 md:h-5" />
        </button>
        <button 
          onClick={() => setIsCartOpen(true)}
          className="relative p-2.5 md:p-3 bg-slate-50 text-slate-600 rounded-2xl hover:bg-slate-100 transition-all"
        >
          <ShoppingCart size={18} className="md:w-5 md:h-5" />
          {cart.length > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-600 text-white text-[10px] font-black w-4 h-4 md:w-5 md:h-5 flex items-center justify-center rounded-full border border-white md:border-2">
              {cart.reduce((acc, item) => acc + item.quantity, 0)}
            </span>
          )}
        </button>
        <Link 
          to={user ? "/profile" : "/signup"} 
          className="flex items-center space-x-2 pl-2 md:pl-4 border-l border-slate-100"
        >
          <div className="w-10 h-10 md:w-12 md:h-12 bg-indigo-50 rounded-2xl flex items-center justify-center text-indigo-600 border border-indigo-100 shadow-sm">
            {user?.photoURL ? (
              <img src={user.photoURL} alt="" className="w-full h-full rounded-2xl object-cover" />
            ) : (
              <User size={18} className="md:w-5 md:h-5" />
            )}
          </div>
        </Link>
      </div>
    </nav>
  );
};
